import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Camera, ScanLine, Ruler, Lightbulb, CheckCircle2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TIPS = [
  { icon: ScanLine, title: "Illuminazione uniforme", desc: "Evita ombre e riflessi. Usa luce diffusa, non diretta sul foglio." },
  { icon: Camera, title: "Inquadratura frontale", desc: "Fotocamera perfettamente parallela al documento, senza prospettiva." },
  { icon: Ruler, title: "Includi il righello grafico", desc: "Assicurati che la scala metrica sia visibile per la calibrazione 1:100." },
  { icon: Lightbulb, title: "Alta risoluzione", desc: "Minimo 300 DPI. Il foglio deve riempire l'inquadratura senza tagli." },
];

export const GuidedScanModal = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#121824] border-slate-800 text-slate-100 max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <ScanLine className="h-5 w-5 text-cyan-400" /> Guida all'acquisizione di qualità
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm text-slate-400 -mt-1">
        Segui questi suggerimenti per una ripresa fotografica ottimale della planimetria di concessione.
      </p>
      <div className="space-y-3 mt-2">
        {TIPS.map((t) => (
          <div key={t.title} className="flex gap-3 p-3 rounded-lg bg-[#0B0F17] border border-slate-800">
            <div className="h-9 w-9 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
              <t.icon className="h-4.5 w-4.5 text-cyan-400" />
            </div>
            <div>
              <p className="font-medium text-sm">{t.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20 mt-1">
        <CheckCircle2 className="h-4 w-4 text-cyan-400 shrink-0" />
        <p className="text-xs text-cyan-200">Dopo il caricamento l'immagine verrà ridimensionata automaticamente in scala 1:100.</p>
      </div>
    </DialogContent>
  </Dialog>
);

// Camera capture using getUserMedia
export const CameraCaptureModal = ({ open, onOpenChange, onCapture }) => {
  const [stream, setStream] = useState(null);
  const videoRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => {
        if (!active) { s.getTracks().forEach((t) => t.stop()); return; }
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => toast.error("Fotocamera non disponibile"));
    return () => {
      active = false;
      setStream((s) => { s?.getTracks().forEach((t) => t.stop()); return null; });
    };
  }, [open]);

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `scatto-${Date.now()}.jpg`, { type: "image/jpeg" });
        onCapture(file);
        onOpenChange(false);
      }
    }, "image/jpeg", 0.92);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-800 text-slate-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Scatto guidato</DialogTitle>
        </DialogHeader>
        <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
          <video ref={videoRef} autoPlay playsInline className="w-full h-full object-contain" />
          <div className="absolute inset-6 border-2 border-dashed border-cyan-400/60 rounded-lg pointer-events-none" />
        </div>
        <div className="flex justify-center gap-3">
          <button
            data-testid="camera-shutter-button"
            onClick={capture}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-500 text-slate-900 font-semibold hover:bg-cyan-400 transition-colors"
          >
            <Camera className="h-5 w-5" /> Scatta
          </button>
          <button
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
          >
            <X className="h-4 w-4" /> Annulla
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
